/**
 * Las secciones de WhatsApp oficial, en el orden en que las muestra el menú lateral.
 *
 * Hasta el 07/10/2026 eran pestañas adentro de la página (`SubNavOficial`), debajo del ítem «WhatsApp oficial» del
 * menú: para ir de Campañas a Estadísticas había que entrar al ítem y después buscar la pestaña, y en el celular
 * las 8 no entraban. Pedido de Alan: que el menú diga «WhatsApp» y abajo cada sección, para moverse desde ahí.
 *
 * Los labels van cortos porque el menú expandido deja ~117 px de texto: «Bajas y consentimientos» no entra y
 * queda en el `title`. El orden es el de las pestañas de antes, para no mover lo que ya se sabe dónde está.
 *
 * «Base de datos» salió del menú el 17/10/2026 (pedido de Alan: «todo lo toma desde Público»): las cargas con
 * consentimiento y la ficha de cada persona están en Público.
 */

import type { LucideIcon } from "lucide-react";
import { BarChart3, FileText, Flame, Images, LayoutDashboard, MessagesSquare, Send, Settings, ShieldCheck, Wallet } from "lucide-react";
import { useCuentaOficialOpcional, type CuentaOficialCtx } from "../contexto";
import { cuentaDelModo } from "../logic/conexion";

export const R_OFICIAL = "/activa/oficial";

export interface SeccionOficial {
  label: string;
  /** Lo que dice el tooltip con el menú colapsado y el `aria-label`. */
  title: string;
  path: string;
  icono: LucideIcon;
  /** `true` sólo para el Resumen: las demás tienen subrutas (/plantillas/:id…) y tienen que seguir pintadas adentro. */
  end: boolean;
  /** Qué aviso lleva el ítem: el contador de Conversaciones o el punto de Configuración. */
  aviso?: "por-contestar" | "revisar-config";
}

/**
 * Para el equipo, el Resumen se llama así: su «Inicio» es el de Activa, arriba de todo en el menú (decisión de Alan del
 * 13/10/2026: un solo Inicio). A una persona de un cliente le sigue diciendo «Inicio» (`SECCION_INICIO_CLIENTE`): para
 * ella es la página de entrada.
 */
export const SECCIONES_OFICIAL: SeccionOficial[] = [
  { label: "Resumen", title: "Resumen de WhatsApp", path: R_OFICIAL, icono: LayoutDashboard, end: true },
  { label: "Plantillas", title: "Plantillas", path: `${R_OFICIAL}/plantillas`, icono: FileText, end: false },
  // fase 2 (24/10/2026): las fotos de las plantillas, con su link. Del equipo: a un cliente no se le muestra (`seccionesOficialPara`).
  { label: "Imágenes", title: "Imágenes de las plantillas", path: `${R_OFICIAL}/imagenes`, icono: Images, end: false },
  { label: "Campañas", title: "Campañas de WhatsApp", path: `${R_OFICIAL}/campanas`, icono: Send, end: false },
  { label: "Conversaciones", title: "Conversaciones", path: `${R_OFICIAL}/conversaciones`, icono: MessagesSquare, end: false, aviso: "por-contestar" },
  { label: "Estadísticas", title: "Estadísticas", path: `${R_OFICIAL}/estadisticas`, icono: BarChart3, end: false },
  { label: "Calentamiento", title: "Calentamiento del número", path: `${R_OFICIAL}/calentamiento`, icono: Flame, end: false },
  { label: "Bajas", title: "Bajas y consentimientos", path: `${R_OFICIAL}/bajas`, icono: ShieldCheck, end: false },
  { label: "Configuración", title: "Configuración de WhatsApp", path: `${R_OFICIAL}/configuracion`, icono: Settings, end: false, aviso: "revisar-config" },
];

/**
 * Lo que ve en lugar de «Configuración» una persona de un cliente (Accesos, rol «comercio»): no configura nada, y de
 * Configuración sólo le toca su saldo y sus movimientos (`ConfiguracionOficialPage` le muestra nada más que esa
 * sección). Sin el punto de «revisar»: lo que avisa (la conexión, la marca frenada) lo arregla el equipo.
 */
export const SECCION_SALDO_CLIENTE: SeccionOficial = {
  label: "Saldo",
  title: "Saldo y movimientos",
  path: `${R_OFICIAL}/configuracion?seccion=saldo`,
  icono: Wallet,
  end: false,
};

/** El Resumen, para una persona de un cliente: su página de entrada, como siempre. */
export const SECCION_INICIO_CLIENTE: SeccionOficial = { ...SECCIONES_OFICIAL[0], label: "Inicio", title: "Inicio de WhatsApp" };

/**
 * Las secciones del menú según quién opera: el equipo, todas; una persona de un cliente, «Inicio» en vez de «Resumen»,
 * «Saldo» en vez de «Configuración» y sin «Imágenes» (las sube el equipo).
 */
export function seccionesOficialPara(equipo: boolean): SeccionOficial[] {
  if (equipo) return SECCIONES_OFICIAL;
  return SECCIONES_OFICIAL.filter((s) => s.path !== `${R_OFICIAL}/imagenes`).map((s) => (s.path === R_OFICIAL ? SECCION_INICIO_CLIENTE : s.path === `${R_OFICIAL}/configuracion` ? SECCION_SALDO_CLIENTE : s));
}

/**
 * Si «Configuración» lleva el punto de «hay algo para revisar». Sólo con una cuenta: mientras carga la primera vez no
 * se sabe nada, y un punto que aparece y desaparece enseña a ignorarlo. Por lo mismo, mientras se vuelve a pedir
 * (`cargando` con la cuenta de antes) vale lo que ya se sabía: la cuenta se vuelve a pedir en cada entrada a la
 * sección (`ProveedorCuenta`), y con `cargando` el punto se apagaba y volvía a prenderse cada vez que se entraba a
 * WhatsApp. «Conectada» con la regla del backend: una verificada en simulado, con el servidor en twilio, también pide
 * revisar.
 */
export function hayQueRevisarConfig(ctx: Pick<CuentaOficialCtx, "cargando" | "cuenta" | "tieneCuenta"> | null): boolean {
  if (!ctx || !ctx.cuenta) return false;
  return !ctx.tieneCuenta || !cuentaDelModo(ctx.cuenta) || !!ctx.cuenta.frenada?.activa;
}

/** El punto de «Configuración» para el menú. Sin `CuentaOficialProvider` arriba no avisa nada. */
export function useRevisarConfiguracion(): boolean {
  return hayQueRevisarConfig(useCuentaOficialOpcional());
}
